import {
  translationAdminControllerDeleteOne,
  translationAdminControllerList,
  translationAdminControllerUpdateOne,
  type Translation,
  unwrapApiResponse,
} from "@cryptors/api-client";
import { useNotification } from "@refinedev/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const getTranslations = async (): Promise<Translation[]> => {
  const data = await unwrapApiResponse(
    translationAdminControllerList<true>({
      query: { limit: 10000 },
      throwOnError: true,
    }),
  );
  return Array.isArray(data) ? data : (data as { data: Translation[] }).data;
};

export const useGetTranslations = () => {
  return useQuery({
    initialData: [],
    queryKey: ["translations"],
    queryFn: () => getTranslations(),
  });
};

export const updateTranslation = async (data: {
  id: number;
  value: string;
}) => {
  return unwrapApiResponse(
    translationAdminControllerUpdateOne<true>({
      body: { value: data.value },
      path: { id: String(data.id) },
      throwOnError: true,
    }),
  );
};

export const useUpdateTranslation = () => {
  const { open } = useNotification();

  return useMutation({
    mutationFn: updateTranslation,
    onSuccess: () => {
      open?.({
        type: "success",
        message: "Modification réussie",
        description: "La modification a été effectuée avec succès",
      });
    },
    onError: () => {
      open?.({
        type: "error",
        message: "Echec de la modification",
        description: "Cette action a échoué. Veuillez réessayer",
      });
    },
  });
};

export const deleteTranslation = async (id: number) => {
  return unwrapApiResponse(
    translationAdminControllerDeleteOne<true>({
      path: { id: String(id) },
      throwOnError: true,
    }),
  );
};

export const useDeleteTranslation = () => {
  const { open } = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTranslation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["translations"] });
      open?.({
        type: "success",
        message: "Suppression réussie",
        description: "La traduction a été supprimée",
      });
    },
    onError: () => {
      open?.({
        type: "error",
        message: "Echec de la suppression",
        description: "Cette action a échoué. Veuillez réessayer",
      });
    },
  });
};
